import React, { useContext, useEffect, useRef, useState } from "react";
import { ClipLoader } from "react-spinners";

import { BatteryStatus } from "../Dashboard/categories/BatteryStatus";
import { Building } from "../Dashboard/categories/Building";
import { CarbonEmissions } from "../Dashboard/categories/CarbonEmission";
import { DemandResponse } from "../Dashboard/categories/DemandResponse";
import { EVCharging } from "../Dashboard/categories/EVCharging";
import { SolarArray } from "../Dashboard/categories/SolarArray";
import { DashboardPageHeader } from "../Dashboard/DashboardPageHeader";
import { AccessDeniedNotice } from "../Shared/AccessDeniedNotice";
import { ZeosApiContext } from "../../context/ZeosApiContext";
import { useDataFromParams } from "../../hooks/useDataFromParams";
import {
  DEFAULT_DASHBOARD_TIME_RANGE,
  DEFAULT_UNAVAILABLE_TIME_RANGES,
  coerceDashboardTimeRange,
  dashboardOverviewMatchesSelection,
  getDashboardDateRange,
  hasMonitor,
  isDashboardOverviewSuccess,
  keepLastGoodDashboardOverview,
  tileTitleMatchesSearch,
} from "../../services/helpers/DashboardHelpers";
import { dashboardTileHasFeed } from "../../services/helpers/DashboardTileHelpers";
import { createLogger } from "../../services/Logger";
import { IDashboardDataResponse } from "../../services/zeos-api/contracts/Dashboard";
import { IZeosApiResponse } from "../../services/zeos-api/contracts/ZeosApi";
import { MonitorTypeEnum } from "../../services/zeos-api/enums/Monitor";

const log = createLogger("MainDashboard");

// DB-R6: avoids refetching the overview this session has already loaded recently
// (e.g. toggling the time range back and forth).
const OVERVIEW_CACHE_TTL_MS = 60_000;
const overviewCache = new Map<
  string,
  { response: IZeosApiResponse<IDashboardDataResponse>; fetchedAt: number }
>();

/** Drop cached overviews for a property so a fresh connect isn't masked by a stale cache hit. */
export const invalidateDashboardOverviewCache = (propertyId: number) => {
  const prefix = `${propertyId}:`;
  for (const key of overviewCache.keys()) {
    if (key.startsWith(prefix)) {
      overviewCache.delete(key);
    }
  }
};

type DashboardOverviewState = {
  propertyId: number;
  timeRange: string;
  data: IDashboardDataResponse;
};

export const MainDashboard = () => {
  const api = useContext(ZeosApiContext);
  const [enableBuilding, setEnableBuilding] = useState(false);
  const [enableCarbon, setEnableCarbon] = useState(false);
  const [enableDemand, setEnableDemand] = useState(false);
  const [enableEv, setEnableEv] = useState(false);
  const [enableSolar, setEnableSolar] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardOverview, setDashboardOverview] = useState<
    DashboardOverviewState | undefined
  >();
  const [unlockedBatteryStatus, setUnlockedBatteryStatus] = useState(false);
  const [timeRange, setTimeRange] = useState(DEFAULT_DASHBOARD_TIME_RANGE);
  const [unavailableTimeRanges, setUnavailableTimeRanges] = useState<string[]>(
    DEFAULT_UNAVAILABLE_TIME_RANGES
  );
  const unavailableTimeRangesRef = useRef(unavailableTimeRanges);
  unavailableTimeRangesRef.current = unavailableTimeRanges;
  const [searchQuery, setSearchQuery] = useState("");

  const { property, propertyPending, propertyAccessDenied } = useDataFromParams();

  useEffect(() => {
    setEnableSolar(
      property ? hasMonitor(MonitorTypeEnum.Solar, property) : false
    );
    setEnableBuilding(
      property ? hasMonitor(MonitorTypeEnum.Building, property) : false
    );
    setEnableEv(property ? hasMonitor(MonitorTypeEnum.EV, property) : false);
    setEnableCarbon(
      property ? hasMonitor(MonitorTypeEnum.Carbon, property) : false
    );
    setLoading(false);
  }, [property]);

  useEffect(() => {
    unavailableTimeRangesRef.current = DEFAULT_UNAVAILABLE_TIME_RANGES;
    setUnavailableTimeRanges(DEFAULT_UNAVAILABLE_TIME_RANGES);
    setEnableDemand(false);
    setUnlockedBatteryStatus(false);
  }, [property?.propertyId]);

  useEffect(() => {
    let cancelled = false;
    const fetchDashboard = async () => {
      const propertyId = property?.propertyId;
      if (!propertyId) {
        if (propertyPending) {
          setDashboardLoading(true);
          return;
        }
        setDashboardOverview(undefined);
        setDashboardLoading(false);
        return;
      }

      const effectiveRange = coerceDashboardTimeRange(
        timeRange,
        unavailableTimeRangesRef.current
      );
      if (effectiveRange !== timeRange) {
        setTimeRange(effectiveRange);
        return;
      }

      const { startDate, endDate } = getDashboardDateRange(effectiveRange);
      const started = performance.now();
      const cacheKey = `${propertyId}:${effectiveRange}`;
      try {
        setDashboardLoading(true);
        const cached = overviewCache.get(cacheKey);
        const isCacheHit =
          !!cached && Date.now() - cached.fetchedAt < OVERVIEW_CACHE_TTL_MS;
        const res = isCacheHit
          ? cached.response
          : await api.Dashboard.getDashboardData(
              propertyId,
              startDate,
              endDate
            );
        if (cancelled) {
          return;
        }
        if (!isDashboardOverviewSuccess(res.zeosStatus, res.data)) {
          log.error("Dashboard overview returned without data", {
            propertyId,
            timeRange: effectiveRange,
            startDate,
            endDate,
            elapsedMs: Math.round(performance.now() - started),
            zeosStatus: res.zeosStatus,
            message: res.message,
          });
          setDashboardOverview((prior) =>
            keepLastGoodDashboardOverview(prior, propertyId, effectiveRange)
          );
          return;
        }
        if (!isCacheHit) {
          overviewCache.set(cacheKey, { response: res, fetchedAt: Date.now() });
        }
        log.info("Dashboard overview loaded", {
          propertyId,
          timeRange: effectiveRange,
          startDate,
          endDate,
          elapsedMs: Math.round(performance.now() - started),
          cached: isCacheHit,
          building: res.data.building?.status,
          solar: res.data.solar?.status,
          carbon: res.data.carbon?.status,
          ev: res.data.ev?.status,
          battery: res.data.battery?.status,
          demandResponse: res.data.demandResponse?.status,
        });
        const nextUnavailable =
          res.data.unavailableTimeRanges ?? DEFAULT_UNAVAILABLE_TIME_RANGES;
        unavailableTimeRangesRef.current = nextUnavailable;
        setDashboardOverview({
          propertyId,
          timeRange: effectiveRange,
          data: res.data,
        });
        setUnavailableTimeRanges(nextUnavailable);
        const snapped = coerceDashboardTimeRange(
          effectiveRange,
          nextUnavailable
        );
        if (snapped !== effectiveRange) {
          setTimeRange(snapped);
        }
      } catch (err) {
        log.error("Failed to load dashboard overview", {
          propertyId,
          elapsedMs: Math.round(performance.now() - started),
          error: err,
        });
        if (!cancelled) {
          setDashboardOverview((prior) =>
            keepLastGoodDashboardOverview(prior, propertyId, effectiveRange)
          );
        }
      } finally {
        if (!cancelled) {
          setDashboardLoading(false);
        }
      }
    };
    fetchDashboard();
    return () => {
      cancelled = true;
    };
  }, [
    property?.propertyId,
    propertyPending,
    timeRange,
    api.Dashboard,
  ]);

  const dashboardData =
    dashboardOverview &&
    dashboardOverviewMatchesSelection(
      dashboardOverview,
      property?.propertyId,
      timeRange
    )
      ? dashboardOverview.data
      : undefined;
  const tilesLoading =
    (dashboardLoading || propertyPending) && !dashboardData;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <ClipLoader color="#f97316" size={50} />
      </div>
    );
  }

  const tiles = [
    {
      key: "solar",
      title: "Power Generation",
      className: "col-span-1 lg:col-span-1 xl:col-span-6",
      render: () => (
        <SolarArray
          unlocked={
            enableSolar || dashboardTileHasFeed(dashboardData?.solar?.status)
          }
          setUnlocked={setEnableSolar}
          tileResult={dashboardData?.solar}
          loading={tilesLoading}
        />
      ),
    },
    {
      key: "building",
      title: "Building Performance",
      className: "col-span-1 lg:col-span-1 xl:col-span-6",
      render: () => (
        <Building
          unlocked={
            enableBuilding ||
            dashboardTileHasFeed(dashboardData?.building?.status)
          }
          setUnlocked={setEnableBuilding}
          tileResult={dashboardData?.building}
          loading={tilesLoading}
          propertyId={property?.propertyId}
          companyId={property?.companyId}
        />
      ),
    },
    {
      key: "ev",
      title: "EV Charging",
      className: "col-span-1 lg:col-span-1 xl:col-span-6",
      render: () => (
        <EVCharging
          unlocked={enableEv}
          setUnlocked={setEnableEv}
          tileResult={dashboardData?.ev}
          loading={tilesLoading}
        />
      ),
    },
    {
      key: "carbon",
      title: "Carbon Emissions",
      className: "col-span-1 lg:col-span-1 xl:col-span-6",
      render: () => (
        <CarbonEmissions
          unlocked={
            enableCarbon || dashboardTileHasFeed(dashboardData?.carbon?.status)
          }
          setUnlocked={setEnableCarbon}
          tileResult={dashboardData?.carbon}
          loading={tilesLoading}
        />
      ),
    },
    {
      key: "demand",
      title: "Demand Response",
      className: "col-span-1 lg:col-span-1 xl:col-span-6",
      render: () => (
        <DemandResponse
          unlocked={enableDemand}
          setUnlocked={setEnableDemand}
          tileResult={dashboardData?.demandResponse}
          loading={tilesLoading}
        />
      ),
    },
    {
      key: "battery",
      title: "Battery Status",
      className: "col-span-1 lg:col-span-1 xl:col-span-6",
      render: () => (
        <BatteryStatus
          unlocked={unlockedBatteryStatus}
          setUnlocked={setUnlockedBatteryStatus}
          tileResult={dashboardData?.battery}
          loading={tilesLoading}
        />
      ),
    },
  ];

  const visibleTiles = tiles.filter((tile) =>
    tileTitleMatchesSearch(tile.title, searchQuery)
  );

  return (
    <div>
      <DashboardPageHeader
        tailLabel="Main Dashboard"
        timeRange={timeRange}
        onTimeRangeChange={setTimeRange}
        unavailableTimeRanges={unavailableTimeRanges}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />
      <div className="px-4 py-4">
        <div className="mx-auto w-full max-w-[1600px] 2xl:max-w-[1840px] xl:px-6 2xl:px-8">
          <div className="gap-6">
            <div className="grid gap-6 grid-cols-1 lg:grid-cols-2 xl:grid-cols-12">
              {propertyAccessDenied ? (
                <div className="col-span-full">
                  <AccessDeniedNotice resource="property" />
                </div>
              ) : visibleTiles.length === 0 ? (
                <div className="col-span-full text-center text-zeos-grey-md py-10">
                  No tiles match "{searchQuery.trim()}".
                </div>
              ) : (
                visibleTiles.map((tile) => (
                  <div key={tile.key} className={tile.className}>
                    {tile.render()}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
